// @ts-check

/**
 * @typedef {object} SyncOptions
 * @property {HTMLElement} [fadeContainer] - Element to set --fade-start/--fade-end CSS custom properties on
 */

/**
 * Syncs a Swiper instance with a pager element (dots variant).
 * Keeps dot count, active dot, and prev/next button states in sync
 * with Swiper's actual page count — including on resize.
 * @param {object} swiper - The Swiper instance
 * @param {HTMLElement} pagerElement - The pager DOM element
 * @param {SyncOptions} [options]
 * @returns {{ destroy: () => void }} Cleanup handle
 */
export function syncPagerWithSwiper(swiper, pagerElement, options) {
	const fadeContainer = options?.fadeContainer;

	/**
	 * Map from swiper snap index → effective page index.
	 * Snap points closer than spaceBetween are merged into one page.
	 * @type {number[]}
	 */
	let snapToPage = [];
	let effectivePageCount = 0;

	/**
	 *
	 */
	function updatePageMap() {
		const grid = swiper.snapGrid;
		const minDistance = swiper.params.spaceBetween || 1;

		snapToPage = [0];
		let pageIndex = 0;

		for (let i = 1; i < grid.length; i++) {
			if (grid[i] - grid[i - 1] >= minDistance) {
				pageIndex++;
			}
			snapToPage.push(pageIndex);
		}

		effectivePageCount = pageIndex + 1;
	}

	/**
	 *
	 */
	function updateDots() {
		const dotsContainer = pagerElement.querySelector(".PositionIndicator");
		if (!dotsContainer) return;

		updatePageMap();

		const currentDotCount = dotsContainer.children.length;
		if (currentDotCount === effectivePageCount) return;

		dotsContainer.innerHTML = "";

		const activePage = snapToPage[swiper.snapIndex] ?? 0;
		for (let i = 0; i < effectivePageCount; i++) {
			const dot = document.createElement("div");
			dot.className = "PositionIndicator-dot";
			if (i === activePage) {
				dot.classList.add("PositionIndicator-dot--active");
			}
			dotsContainer.appendChild(dot);
		}
	}

	/**
	 *
	 */
	function updateActiveDot() {
		const dots = pagerElement.querySelectorAll(".PositionIndicator-dot");
		const activePage = snapToPage[swiper.snapIndex] ?? 0;

		dots.forEach((dot, index) => {
			dot.classList.toggle(
				"PositionIndicator-dot--active",
				index === activePage,
			);
		});
	}

	/**
	 *
	 */
	function updateButtonStates() {
		const prevButton = pagerElement.querySelector(".Pager-button--prev");
		const nextButton = pagerElement.querySelector(".Pager-button--next");

		if (prevButton) {
			const disabled = swiper.isBeginning;
			prevButton.classList.toggle("Pager-button--disabled", disabled);
			if (disabled) {
				prevButton.setAttribute("aria-disabled", "true");
				prevButton.setAttribute("tabindex", "-1");
			} else {
				prevButton.removeAttribute("aria-disabled");
				prevButton.removeAttribute("tabindex");
			}
		}

		if (nextButton) {
			const disabled = swiper.isEnd;
			nextButton.classList.toggle("Pager-button--disabled", disabled);
			if (disabled) {
				nextButton.setAttribute("aria-disabled", "true");
				nextButton.setAttribute("tabindex", "-1");
			} else {
				nextButton.removeAttribute("aria-disabled");
				nextButton.removeAttribute("tabindex");
			}
		}
	}

	/**
	 *
	 */
	function updateFade() {
		if (!fadeContainer) return;
		const size =
			getComputedStyle(fadeContainer).getPropertyValue("--fade-size").trim() ||
			"40px";
		fadeContainer.style.setProperty("--fade-end", swiper.isEnd ? "0px" : size);
	}

	/**
	 *
	 */
	function onSlideChange() {
		updateActiveDot();
		updateButtonStates();
		updateFade();
	}

	/**
	 *
	 */
	function onLayoutChange() {
		updateDots();
		updateButtonStates();
		updateFade();
	}

	/** @param {MouseEvent} event */
	function onPagerClick(event) {
		const link = /** @type {HTMLElement} */ (event.target).closest("a");
		if (!link) return;

		const isPrev = link.classList.contains("Pager-button--prev");
		const isNext = link.classList.contains("Pager-button--next");

		if (!isPrev && !isNext) return;
		if (link.classList.contains("Pager-button--disabled")) return;

		event.preventDefault();

		if (isPrev) {
			swiper.slidePrev();
		} else {
			swiper.slideNext();
		}
	}

	// Listen to Swiper events
	swiper.on("snapIndexChange", onSlideChange);
	swiper.on("snapGridLengthChange", onLayoutChange);
	swiper.on("resize", onLayoutChange);

	// Listen to pager clicks
	pagerElement.addEventListener("click", onPagerClick);

	// Initial sync
	updateDots();
	updateButtonStates();
	updateFade();

	return {
		destroy() {
			swiper.off("snapIndexChange", onSlideChange);
			swiper.off("snapGridLengthChange", onLayoutChange);
			swiper.off("resize", onLayoutChange);
			pagerElement.removeEventListener("click", onPagerClick);
		},
	};
}
